import React from 'react';
import {
    Label
} from 'shared/components/ui/label';
import {
    Input
} from 'shared/components/ui/input';
import {
    NewCombobox
} from 'shared/components/ui/combobox';
import {
    FaWhatsapp
} from 'react-icons/fa';
import AssistantSelect from '../AssistantSelect';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { cn } from 'shared/lib/utils';

interface WhatsAppConfigSidebarProps {
    provider: 'twilio' | 'baileys' | 'coexistence';
    setProvider: (value: 'twilio' | 'baileys' | 'coexistence') => void;
    topic: string;
    assistantId: string;
    address: string;
    hideAssistant?: boolean;
    receiveEnabled: boolean;
    sendMode: 'auto' | 'tool' | 'disabled';
    onTopicChange: (value: string) => void;
    onAssistantChange: (value: string) => void;
    onNumberChange: (value: string) => void;
    onReceiveEnabledChange: (value: boolean) => void;
    onSendModeChange: (value: 'auto' | 'tool' | 'disabled') => void;
    connectionMethod?: 'qr' | 'phone';
    setConnectionMethod?: (value: 'qr' | 'phone') => void;
    phoneForPairing?: string;
    onPhoneForPairingChange?: (value: string) => void;
    isUpdate?: boolean;
    isListener?: boolean;
    // Co-existence only: the Meta Cloud API Phone Number ID. Kept separate from
    // `address` (the display number) because the two are different ids that are both
    // numeric — storing one where the other belongs fails silently.
    phoneNumberId?: string;
    onPhoneNumberIdChange?: (value: string) => void;
}

interface StepProps {
    num: string;
    connector?: boolean;
    children: React.ReactNode;
}

const Step: React.FC<StepProps> = ({ num, connector = true, children }) => (
    <div className="relative flex gap-3.5">
        <div className="flex flex-col items-center shrink-0">
            <div className="w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-[9px] font-bold z-10 shrink-0">
                {num}
            </div>
            {connector && <div className="w-px flex-1 bg-border/60 mt-1 min-h-[12px]" />}
        </div>
        <div className={cn('flex-grow', connector ? 'pb-5' : 'pb-0')}>
            {children}
        </div>
    </div>
);

const WhatsAppConfigSidebar: React.FC<WhatsAppConfigSidebarProps> = ({
    provider,
    setProvider,
    topic,
    assistantId,
    address,
    hideAssistant,
    receiveEnabled,
    sendMode,
    onTopicChange,
    onAssistantChange,
    onNumberChange,
    onReceiveEnabledChange,
    onSendModeChange,
    connectionMethod = 'qr',
    setConnectionMethod,
    phoneForPairing = '',
    onPhoneForPairingChange,
    isUpdate = false,
    isListener = false,
    phoneNumberId = '',
    onPhoneNumberIdChange,
}) => {
    const showConnectionMethod = provider === 'baileys' && !isUpdate;
    // Co-existence has its own step 04 (display number + Meta Phone Number ID).
    const showCoexistenceFields = provider === 'coexistence';
    // Step 03 (Receive/Send) has a connector when more steps follow
    const step03HasConnector = showConnectionMethod || provider === 'twilio' || showCoexistenceFields || !hideAssistant;
    // Step 04 (Connection Method for Baileys / Phone Number for Twilio / number + id
    // for co-existence) has connector when AI follows
    const step04HasConnector = !hideAssistant;
    // AI Assistant step number depends on whether step 04 exists
    const assistantStepNum = (showConnectionMethod || provider === 'twilio' || showCoexistenceFields) ? '05' : '04';

    return (
        <aside className="sm:w-[360px] shrink-0 bg-muted/40 border-b sm:border-b-0 sm:border-r border-border p-6 flex flex-col gap-0 sm:overflow-y-auto min-h-0">
            {/* Header */}
            <div className="mb-6 space-y-1">
                <div className="inline-flex items-center gap-2 px-2 py-1 bg-primary/10 rounded-md text-primary mb-1">
                    <FaWhatsapp size={14} />
                    <span className="text-[10px] font-bold uppercase tracking-wider">Channel Configuration</span>
                </div>
                <h3 className="text-sm font-semibold text-foreground px-1">Configure Instance</h3>
            </div>

            {/* Numbered steps */}
            <div className="flex flex-col">

                {/* 01 — Provider */}
                <Step num="01" connector>
                    <Label className="text-[11px] font-semibold text-muted-foreground block mb-1.5">
                        WhatsApp Provider <span className="text-destructive">*</span>
                    </Label>
                    <NewCombobox
                        className="mt-0.5"
                        defaultDisplayed="Select Provider"
                        items={[
                            { value: 'twilio', name: 'WhatsApp API via Twilio' },
                            { value: 'baileys', name: 'WhatsApp Web' },
                            { value: 'coexistence', name: 'WhatsApp Business (Co-existence)' },
                        ]}
                        setter={(value: string) => setProvider(value as 'twilio' | 'baileys' | 'coexistence')}
                        selectedValue={provider}
                        comboboxWidth="w-full"
                        noSearch
                    />
                    <p className="text-[10px] text-muted-foreground mt-1 italic">
                        {provider === 'twilio'
                            ? 'Best for official high volume messaging.'
                            : provider === 'coexistence'
                            ? 'Official Cloud API, with the number still live in the WhatsApp Business app. Text and templates only.'
                            : 'Connect your phone by QR or pairing code.'}
                    </p>
                </Step>

                {/* 02 — Name */}
                <Step num="02" connector>
                    <Label className="text-[11px] font-semibold text-muted-foreground block mb-1.5">
                        {provider === 'baileys' ? 'Instance Name' : 'Display Name'} <span className="text-destructive">*</span>
                    </Label>
                    <Input
                        value={topic ?? ''}
                        onChange={(e) => onTopicChange(e.target.value)}
                        placeholder="e.g. Support"
                        className="h-9 text-xs"
                    />
                </Step>

                {/* 03 — Receive / Send config. A listener is receive-only and never sends,
                    so its send-mode and receive toggles are fixed — show a short summary
                    instead of the controls to keep the setup focused. */}
                <Step num="03" connector={step03HasConnector}>
                    {isListener ? (
                        <>
                            <Label className="text-[11px] font-semibold text-muted-foreground block mb-1.5">
                                How it works
                            </Label>
                            <div className="rounded-lg border border-border bg-background/60 p-2.5">
                                <p className="text-[11px] text-foreground font-medium">Receive only listener</p>
                                <p className="text-[10px] text-muted-foreground mt-0.5 leading-snug">
                                    Collects incoming messages into knowledge. It never sends or replies. Choose what to collect on the right.
                                </p>
                            </div>
                        </>
                    ) : (
                        <>
                            <Label className="text-[11px] font-semibold text-muted-foreground block mb-1.5">
                                Receive Messages <span className="text-destructive">*</span>
                            </Label>
                            <NewCombobox
                                className="mt-0.5"
                                defaultDisplayed="Select"
                                items={[
                                    { value: 'true', name: 'Yes, receive inbound messages' },
                                    { value: 'false', name: 'No, outbound only' },
                                ]}
                                setter={(v: string) => onReceiveEnabledChange(v === 'true')}
                                selectedValue={String(receiveEnabled)}
                                comboboxWidth="w-full"
                                noSearch
                            />
                            <div className="mt-3">
                                <Label className="text-[11px] font-semibold text-muted-foreground block mb-1.5">
                                    Send Mode <span className="text-destructive">*</span>
                                </Label>
                                <NewCombobox
                                    className="mt-0.5"
                                    defaultDisplayed="Select Mode"
                                    items={[
                                        { value: 'auto', name: 'Auto reply' },
                                        { value: 'tool', name: 'Send via tool call' },
                                        { value: 'disabled', name: 'Never send' },
                                    ]}
                                    setter={(v: string) => onSendModeChange(v as 'auto' | 'tool' | 'disabled')}
                                    selectedValue={sendMode}
                                    comboboxWidth="w-full"
                                    noSearch
                                />
                                <p className="text-[10px] text-muted-foreground mt-1 italic">
                                    {sendMode === 'disabled'
                                        ? 'Channel never sends a reply.'
                                        : sendMode === 'tool'
                                        ? 'Assistant may trigger send via a tool call.'
                                        : 'Assistant replies automatically after processing.'}
                                </p>
                            </div>
                        </>
                    )}
                </Step>

                {/* 04 — Connection Method (Baileys, new channels only) */}
                {showConnectionMethod && (
                    <Step num="04" connector={step04HasConnector}>
                        <Label className="text-[11px] font-semibold text-muted-foreground block mb-1.5">
                            Auth Method <span className="text-destructive">*</span>
                        </Label>
                        <NewCombobox
                            className="mt-0.5"
                            defaultDisplayed="Select Method"
                            items={[
                                { value: 'qr', name: 'QR Code' },
                                { value: 'phone', name: 'Phone Number (Code)' },
                            ]}
                            setter={(value: string) => setConnectionMethod?.(value as 'qr' | 'phone')}
                            selectedValue={connectionMethod}
                            comboboxWidth="w-full"
                            noSearch
                        />
                        <p className="text-[10px] text-muted-foreground mt-1 italic">
                            {connectionMethod === 'phone'
                                ? 'Get an 8 character code to enter in WhatsApp.'
                                : 'Scan a QR code with your phone.'}
                        </p>
                        {connectionMethod === 'phone' && (
                            <div className="mt-2">
                                <Label className="text-[11px] font-semibold text-muted-foreground block mb-1.5">
                                    Phone Number <span className="text-destructive">*</span>
                                </Label>
                                <PhoneInput
                                    country={'my'}
                                    value={phoneForPairing}
                                    onChange={(value) => onPhoneForPairingChange?.(value)}
                                    inputClass="!w-full !h-9 !pl-11 !text-xs !font-normal"
                                    containerClass="!w-full"
                                    buttonClass="!border-border"
                                    specialLabel=""
                                />
                            </div>
                        )}
                    </Step>
                )}

                {/* 04 — Phone Number (Twilio) */}
                {provider === 'twilio' && (
                    <Step num="04" connector={step04HasConnector}>
                        <Label className="text-[11px] font-semibold text-muted-foreground block mb-1.5">
                            Phone Number <span className="text-destructive">*</span>
                        </Label>
                        <PhoneInput
                            country={'my'}
                            value={address?.replace('whatsapp:+', '') || ''}
                            onChange={onNumberChange}
                            inputClass="!w-full !h-9 !pl-11 !text-xs !font-normal"
                            containerClass="!w-full"
                            buttonClass="!border-border"
                            specialLabel=""
                        />
                    </Step>
                )}

                {/* 04 — Display number + Meta Phone Number ID (Co-existence) */}
                {showCoexistenceFields && (
                    <Step num="04" connector={step04HasConnector}>
                        <Label className="text-[11px] font-semibold text-muted-foreground block mb-1.5">
                            WhatsApp Business Number <span className="text-destructive">*</span>
                        </Label>
                        <PhoneInput
                            country={'sg'}
                            value={address || ''}
                            onChange={onNumberChange}
                            inputClass="!w-full !h-9 !pl-11 !text-xs !font-normal"
                            containerClass="!w-full"
                            buttonClass="!border-border"
                            specialLabel=""
                            disabled={isUpdate}
                        />
                        <p className="text-[10px] text-muted-foreground mt-1 italic">
                            The number customers message. Inbound is matched on this, so it cannot
                            change after creation.
                        </p>
                        <div className="mt-3">
                            <Label className="text-[11px] font-semibold text-muted-foreground block mb-1.5">
                                Meta Phone Number ID <span className="text-destructive">*</span>
                            </Label>
                            <Input
                                value={phoneNumberId ?? ''}
                                onChange={(e) => onPhoneNumberIdChange?.(e.target.value.replace(/[^0-9]/g, ''))}
                                placeholder="e.g. 1214218528438822"
                                className="h-9 text-xs"
                                inputMode="numeric"
                            />
                            <p className="text-[10px] text-muted-foreground mt-1 italic">
                                From the Meta app dashboard — not the phone number. This picks which
                                number replies go out from; Meta rejects the dialable number here.
                            </p>
                        </div>
                    </Step>
                )}

                {/* 04/05 — AI Assistant */}
                {!hideAssistant && (
                    <Step num={assistantStepNum} connector={false}>
                        <Label className="text-[11px] font-semibold text-muted-foreground block mb-1.5">
                            AI Assistant <span className="text-destructive">*</span>
                        </Label>
                        <AssistantSelect
                            className="mt-0.5"
                            value={assistantId || ''}
                            onChange={onAssistantChange}
                        />
                        <p className="text-[10px] text-muted-foreground mt-1">Assistant that will handle chats.</p>
                    </Step>
                )}
            </div>
        </aside>
    );
};

export default WhatsAppConfigSidebar;
